import type { Character, MoveRef } from '@/schemas/character';
import type { LevelUpRecord } from '@/schemas/levelUpRecord';
import {
  INITIAL_LEVELUP_DRAFT,
  type LevelUpDraft,
  type LevelUpGrantedChoiceDraft,
  type LevelUpMulticlassPickDraft,
} from './types';

/**
 * Trasforma `LevelUpRecord.spellGrantSelections` (readonly mappa di array
 * readonly) in `LevelUpDraft.spellSelections` con la stessa shape ma garantita
 * la presenza del campo (default `{}` se record vecchio senza il campo).
 */
function buildSpellSelectionsFromRecord(record: LevelUpRecord): LevelUpDraft['spellSelections'] {
  if (record.spellGrantSelections === undefined) return {};
  const entries = Object.entries(record.spellGrantSelections).map(
    ([k, ids]) => [k, [...ids]] as const,
  );
  return Object.fromEntries(entries);
}

/**
 * **FI-46 PR-B**: mapping inverso `LevelUpRecord → LevelUpDraft` per
 * pre-popolare il `LevelUpFlowPage` in modalità edit-retroattivo.
 *
 * Inverso parziale di `buildLevelUpRecord` (`services/levelUp.audit.ts`):
 * - Campi semplici (`statIncreased`, `secondaryArchetypeId`) → copia diretta
 * - `moveAdded` (record è solo ID) → ricostruito cercando il `MoveRef`
 *   completo nelle `characterBeforeRewind.moves`
 * - `grantedChoice` / `multiclassPick` → selectedMoveSetup recuperato
 *   analogamente dal character pre-rewind
 * - `bundleSetups` → ricostruito da `record.movesBundled` + character.moves
 * - `pastTen` → letto da `characterBeforeRewind.pastTen` (NON è nel record:
 *   è uno stato del Character settato nel record di L10 → L11)
 *
 * **FI-46 PR-B+ (2026-05-23)**: `spellSelections` è ora pre-popolato dal
 * campo `record.spellGrantSelections` (popolato da `buildLevelUpRecord`
 * a partire da `LevelUpInput.spellSelections`). Record pre-FI-46 (campo
 * assente) → fallback al comportamento precedente (mappa vuota, utente
 * riseleziona). Niente migration necessaria.
 *
 * @param record `LevelUpRecord` da invertire (record di L{N} che si vuole rifare)
 * @param characterBeforeRewind PG **pre-rewind** (cioè al livello attuale,
 *   con tutta la `levelUpHistory`). Necessario per recuperare i `MoveRef`
 *   completi (setup, narrativeFieldValues, ecc.) che il record non conserva.
 * @returns `LevelUpDraft` pre-popolato con tutti i campi ricostruibili
 */
export function levelUpRecordToDraft(
  record: LevelUpRecord,
  characterBeforeRewind: Character,
): LevelUpDraft {
  const moveAdded = findMoveRef(characterBeforeRewind, record.moveAdded);
  // Se la mossa-trigger non è più nel character (caso patologico: il PG è
  // stato editato manualmente fuori dal flow), fallback al solo ID con
  // selectedChoices vuoto — il flow chiederà il setup all'utente.
  const moveAddedDraft: MoveRef = moveAdded ?? {
    id: record.moveAdded,
    selectedChoices: [],
  };

  const isL11Record = record.levelReached === 11;
  const pastTenDraft = isL11Record ? characterBeforeRewind.pastTen : undefined;

  return {
    ...INITIAL_LEVELUP_DRAFT,
    moveAdded: moveAddedDraft,
    statIncreased: record.statIncreased,
    secondaryArchetypeId: record.secondaryArchetypeAdded,
    pastTen: pastTenDraft,
    grantedChoice: buildGrantedChoiceDraft(record, characterBeforeRewind),
    multiclassPick: buildMulticlassPickDraft(record, characterBeforeRewind),
    spellSelections: buildSpellSelectionsFromRecord(record),
  };
}

/**
 * Ricostruisce `LevelUpGrantedChoiceDraft` dal record audit.
 *
 * - `from-group` / `from-choice-mapping`: la mossa concessa è in
 *   `character.moves` (è stata aggiunta da `applyLevelUp` come nuovo `MoveRef`)
 *   → `selectedMoveSetup` ricostruito da quella `MoveRef`.
 * - `from-choice-move`: la sotto-scelta ha esteso `selectedChoices` di una
 *   mossa esistente (non c'è una nuova `MoveRef`) → niente `selectedMoveSetup`.
 */
function buildGrantedChoiceDraft(
  record: LevelUpRecord,
  character: Character,
): LevelUpGrantedChoiceDraft | undefined {
  if (record.grantedChoice === undefined) return undefined;
  const { selectedOptionId } = record.grantedChoice;
  if (record.grantedChoice.kind === 'from-choice-move') {
    return { selectedOptionId };
  }
  // from-group / from-choice-mapping: cerca la MoveRef della mossa concessa
  const grantedMoveRef = findMoveRef(character, selectedOptionId);
  if (!grantedMoveRef) return { selectedOptionId };
  const setup = extractMoveSetup(grantedMoveRef);
  if (setup === undefined) return { selectedOptionId };
  return { selectedOptionId, selectedMoveSetup: setup };
}

/**
 * Ricostruisce `LevelUpMulticlassPickDraft` dal record audit.
 *
 * - `selectedMoveSetup`: dalla `MoveRef` della mossa-target multiclasse in
 *   `character.moves`.
 * - `bundleSetups`: per ogni mossa del bundle (in `record.movesBundled`) che
 *   ha un setup non-trivial, ricostruisce la entry `moveId → Omit<MoveRef, 'id'>`.
 */
function buildMulticlassPickDraft(
  record: LevelUpRecord,
  character: Character,
): LevelUpMulticlassPickDraft | undefined {
  if (record.multiclassPick === undefined) return undefined;
  const { sourceClassId, selectedMoveId } = record.multiclassPick;
  const draft: LevelUpMulticlassPickDraft = { sourceClassId, selectedMoveId };
  const targetMoveRef = findMoveRef(character, selectedMoveId);
  const targetSetup = targetMoveRef ? extractMoveSetup(targetMoveRef) : undefined;
  if (targetSetup !== undefined) draft.selectedMoveSetup = targetSetup;
  const bundleSetups = buildBundleSetups(record.movesBundled, character);
  if (bundleSetups !== undefined) draft.bundleSetups = bundleSetups;
  return draft;
}

function buildBundleSetups(
  bundledIds: readonly string[],
  character: Character,
): Record<string, Omit<MoveRef, 'id'>> | undefined {
  if (bundledIds.length === 0) return undefined;
  const setups: Record<string, Omit<MoveRef, 'id'>> = {};
  for (const moveId of bundledIds) {
    const ref = findMoveRef(character, moveId);
    if (!ref) continue;
    const setup = extractMoveSetup(ref);
    if (setup !== undefined) setups[moveId] = setup;
  }
  return Object.keys(setups).length > 0 ? setups : undefined;
}

function findMoveRef(character: Character, moveId: string): MoveRef | undefined {
  return character.moves.find((m) => m.id === moveId);
}

/**
 * Estrae `Omit<MoveRef, 'id'>` (setup interattivo) da una `MoveRef`.
 *
 * Ritorna `undefined` se la mossa non ha setup compilato (no choices,
 * no choiceGroups, no narrativeFieldValues, no recruitOutcome) — segnale
 * al chiamante di NON includere `selectedMoveSetup` nel draft.
 */
function extractMoveSetup(ref: MoveRef): Omit<MoveRef, 'id'> | undefined {
  const hasChoices = ref.selectedChoices.length > 0;
  const hasGroups =
    ref.selectedChoiceGroups !== undefined && Object.keys(ref.selectedChoiceGroups).length > 0;
  const hasNarrative =
    ref.narrativeFieldValues !== undefined && Object.keys(ref.narrativeFieldValues).length > 0;
  const hasRecruit = ref.recruitOutcome !== undefined;
  if (!hasChoices && !hasGroups && !hasNarrative && !hasRecruit) return undefined;
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: ref.selectedChoices };
  if (ref.selectedChoiceGroups !== undefined) setup.selectedChoiceGroups = ref.selectedChoiceGroups;
  if (ref.narrativeFieldValues !== undefined) setup.narrativeFieldValues = ref.narrativeFieldValues;
  if (ref.recruitOutcome !== undefined) setup.recruitOutcome = ref.recruitOutcome;
  return setup;
}
